import React, { useState } from 'react';
import { Grid, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Category, CreditCard, Edit } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import { useEditOrder } from '../../EditOrderContext';
import { OutlinedIconButton } from '../../../../../common/ui/OutlinedIconButton';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import {
  AccountData,
  OrderData,
  OrderService,
  RefundablePaymentData,
  RefundOwnerType,
  VoucherOut,
  VoucherService,
} from '../../../../../api';
import { isProjectSelectionPending } from '../../../../../common/ProjectSelectionPendingBadge';
import { ExtraPaymentDialog } from './ExtraPaymentDialog';
import { ExtraItemsSection } from './ExtraItemsSection';
import { ReschedulePenaltiesSection } from './ReschedulePenaltiesSection';
import { ExtraItemsQuickAction } from './ExtraItemsQuickAction';
import { usePriceRenderer } from '../../../../../common/usePriceRenderer';
import { usePriceFormatter } from '../../../../../common/usePriceFormatter';
import { getCurrencySymbol } from '../../../../../utils/utils';
import { UseAuthPermissions } from '../../../../../session/UseAuthPermissions';
import { RefundDialog } from '../../../../../refunds/RefundDialog';
import { RefundsSection } from '../../../../../refunds/RefundsSection';
import { useRefundOverview } from '../../../../../refunds/useRefundOverview';

type PriceBoxProps = {
  // Propagates a server-refreshed order up to the calendar's SWR cache.
  onOrderUpdated?: (order: OrderData) => void;
  focusExtraItems?: boolean;
  requestedAction?: { type: ExtraItemsQuickAction; token: number } | null;
};

export const PriceBox = ({ onOrderUpdated, focusExtraItems, requestedAction }: PriceBoxProps) => {
  const { authState } = useAuth();
  const {
    // @ts-ignore
    editOrderState,
    // @ts-ignore
    setPrice,
    // @ts-ignore
    applyRefreshedOrder,
  } = useEditOrder();
  const priceFormatter = usePriceFormatter();

  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const [isCustomisingPrice, setIsCustomisingPrice] = useState(false);
  const isEditingExistingOrder = editOrderState.order_id !== '';
  const isEditing = !!editOrderState.orderOrVoucher;
  const shouldHideEditIcon = editOrderState.shouldAllowPriceOverride
    ? false
    : isCustomisingPrice || isEditingExistingOrder;
  const orderState = editOrderState;
  const [isAddPaymentItemOpen, setIsAddPaymentItemOpen] = useState(false);
  const savedCardSuffix = (editOrderState.orderOrVoucher as OrderData)?.card_suffix;
  const projectSelectionPending =
    !!editOrderState.orderOrVoucher &&
    isProjectSelectionPending(editOrderState.orderOrVoucher as OrderData, authState.account as unknown as AccountData);

  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  const [refundItem, setRefundItem] = useState<RefundablePaymentData | undefined>(undefined);
  const isVoucher = (editOrderState.orderOrVoucher as VoucherOut)?.code !== undefined;
  const refundOwnerType = isVoucher
    ? RefundOwnerType.VOUCHER
    : isEditingExistingOrder
    ? RefundOwnerType.ORDER
    : undefined;
  const { overview: refundOverview, mutate: mutateRefundOverview } = useRefundOverview(
    refundOwnerType,
    editOrderState.orderOrVoucher?.id,
  );
  const refreshAfterRefund = async () => {
    await mutateRefundOverview();
    if (isVoucher) {
      applyRefreshedOrder(await VoucherService.getVoucherById(editOrderState.orderOrVoucher.id));
    } else {
      const freshOrder = await OrderService.getOrder(editOrderState.order_id);
      applyRefreshedOrder(freshOrder);
      onOrderUpdated?.(freshOrder);
    }
  };
  const { FullPriceBreakdown } = usePriceRenderer({
    orderOrVoucher: editOrderState.orderOrVoucher,
    initialPrice: editOrderState.details.price,
    returningCustomerFromOrderId: editOrderState.returningCustomerFromOrderId,
    extraPaymentLines: editOrderState.extraPaymentsLines,
    shouldAllowPriceOverride: editOrderState.shouldAllowPriceOverride,
    onEditPrice: () => setIsCustomisingPrice(true),
    onOrderCommitted: (freshOrder: OrderData) => {
      applyRefreshedOrder(freshOrder);
      onOrderUpdated?.(freshOrder);
    },
    refreshKey: editOrderState.paymentLinksRefreshKey,
    refundOverview,
    onRefund: setRefundItem,
  });
  return (
    <Grid item container flexDirection="column" mt={0}>
      <Grid item container gap={1} alignItems="center">
        <Grid
          item
          container
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          gap={1.5}
          sx={{ bgcolor: 'background.default', borderRadius: '6px', p: '12px 14px', mt: 1 }}>
          <Grid container item direction="column" gap={0.25} width="auto">
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('calendar.edit_order.order_details.calculatedPrice')}
            </Typography>
            <Grid item dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {priceFormatter(editOrderState.calculatedPrice, false, 'h4')}
            </Grid>
          </Grid>
          {shouldHideEditIcon ? null : (
            <OutlinedIconButton onClick={() => setIsCustomisingPrice(true)} sx={{ width: 44, height: 44 }}>
              <Edit />
            </OutlinedIconButton>
          )}
        </Grid>
        {isEditingExistingOrder && !readOnly && (
          <Grid item container justifyContent="space-between" alignItems="center" mt={2}>
            <Grid item container width="auto" alignItems="center" gap={0.5} flexWrap="nowrap">
              <Typography fontWeight="700">{t('calendar.edit_order.order_details.details')}</Typography>
              {(orderState.orderOrVoucher as OrderData)?.has_saved_card && (
                <Tooltip
                  title={
                    savedCardSuffix
                      ? t('calendar.edit_order.order_details.cardOnFileSuffix', { suffix: savedCardSuffix })
                      : t('calendar.edit_order.order_details.cardOnFile')
                  }
                  arrow>
                  <CreditCard fontSize="small" sx={{ color: 'primary.main' }} />
                </Tooltip>
              )}
              {projectSelectionPending && (
                <Tooltip title={t('calendar.edit_order.order_details.projectNotChosen')} arrow>
                  <Category fontSize="small" sx={{ color: 'warning.main' }} />
                </Tooltip>
              )}
            </Grid>
            <Grid justifyContent="flex-end">
              <Button
                onClick={() => setIsAddPaymentItemOpen(true)}
                variant="outlined"
                style={{ textTransform: 'none' }}>
                {t('calendar.edit_order.order_details.addExtraPayment')}
                <Add />
              </Button>
            </Grid>
          </Grid>
        )}
        {isCustomisingPrice && (
          <Grid container alignItems="center" gap={1}>
            <Typography>{t('calendar.edit_order.order_details.ovveridePrice')}</Typography>
            <Grid item width="100px">
              <TextField
                type="number"
                size="small"
                id="title"
                name="title"
                variant="outlined"
                value={orderState.details.price}
                onChange={(e: any) => setPrice(+e.target.value || 0)}
                InputProps={{
                  endAdornment: currencySymbol,
                  sx: {
                    color: 'text.primary',
                  },
                }}
              />
            </Grid>
          </Grid>
        )}
        {isEditing && (
          <Grid container width="100%">
            <FullPriceBreakdown
              isCustomisingPrice={isCustomisingPrice}
              calculatedFullPrice={editOrderState.calculatedPrice}
            />
          </Grid>
        )}
      </Grid>
      {/* The endpoint requires an existing order_id, so this gate is load-bearing --
          on a create-order intent there is nothing to attach a payment to yet. */}
      {isEditingExistingOrder && (
        <ExtraItemsSection
          orderId={orderState.order_id}
          order={orderState.orderOrVoucher as OrderData}
          orderPageId={editOrderState.orderPage?.id || (orderState.orderOrVoucher as OrderData)?.order_page_id}
          shouldFocus={focusExtraItems}
          requestedAction={requestedAction}
          onCommitted={(freshOrder: OrderData) => {
            applyRefreshedOrder(freshOrder);
            onOrderUpdated?.(freshOrder);
            void mutateRefundOverview();
          }}
        />
      )}
      {isEditingExistingOrder && <ReschedulePenaltiesSection order={orderState.orderOrVoucher as OrderData} />}
      {!!refundOwnerType && <RefundsSection overview={refundOverview} />}
      {!!refundOwnerType && (
        <RefundDialog
          isOpen={!!refundItem}
          onClose={() => setRefundItem(undefined)}
          item={refundItem}
          ownerLabel={
            isVoucher
              ? t('refunds.dialog.voucherTitle', {
                  number: (orderState.orderOrVoucher as VoucherOut).order_number,
                })
              : t('auditlog.entity_info.order', {
                  orderNumber: (orderState.orderOrVoucher as OrderData).order_number,
                })
          }
          voucherBalance={isVoucher ? (orderState.orderOrVoucher as VoucherOut).balance : undefined}
          onRefunded={() => {
            refreshAfterRefund().catch(() => undefined);
          }}
        />
      )}
      {isEditingExistingOrder && (
        <ExtraPaymentDialog
          isOpen={isAddPaymentItemOpen}
          onClose={() => setIsAddPaymentItemOpen(false)}
          orderId={orderState.order_id}
          order={orderState.orderOrVoucher as OrderData}
          onCommitted={(freshOrder: OrderData) => {
            applyRefreshedOrder(freshOrder);
            onOrderUpdated?.(freshOrder);
            void mutateRefundOverview();
          }}
        />
      )}
    </Grid>
  );
};
